import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  LogIn,
  UserPlus,
  PartyPopper,
} from "lucide-react";

type EnrollmentStatus = "loading" | "success" | "error";

const EnrollmentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const sessionId = searchParams.get("session_id");
  const isFree = searchParams.get("free") === "true";
  const freeEmail = searchParams.get("email") || "";

  const [status, setStatus] = useState<EnrollmentStatus>("loading");
  const [enrollmentEmail, setEnrollmentEmail] = useState(freeEmail);
  const [userExists, setUserExists] = useState(false);

  // Auth form state
  const [mode, setMode] = useState<"login" | "register">("register");
  const [password, setPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    if (user) {
      // Already logged in, redirect to dashboard
      navigate("/dashboard", { replace: true });
      return;
    }

    if (isFree && freeEmail) {
      checkUserExists(freeEmail);
      setStatus("success");
      return;
    }

    if (!sessionId) {
      setStatus("error");
      return;
    }

    // Poll for enrollment completion
    const checkEnrollment = async () => {
      const { data } = await supabase
        .rpc("get_enrollment_by_session", { _session_id: sessionId })
        .maybeSingle();

      if (data?.status === "completed") {
        setEnrollmentEmail(data.email);
        await checkUserExists(data.email);
        setStatus("success");
      } else if (data?.status === "failed") {
        setStatus("error");
      } else {
        // Still pending, retry
        setTimeout(checkEnrollment, 2000);
      }
    };

    checkEnrollment();
  }, [sessionId, isFree, user]);

  const checkUserExists = async (email: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", email)
      .maybeSingle();

    if (data) {
      setUserExists(true);
      setMode("login");
    } else {
      setUserExists(false);
      setMode("register");
    }
  };

  const handleLogin = async () => {
    setAuthLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: enrollmentEmail,
        password,
      });
      if (error) throw error;
      toast.success("¡Bienvenido! Redirigiendo...");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error(err.message || "Error al iniciar sesión");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async () => {
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setAuthLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: enrollmentEmail,
        password,
        options: {
          data: { full_name: regName || enrollmentEmail.split("@")[0] },
        },
      });
      if (error) throw error;
      toast.success(
        "Cuenta creada. Revisa tu correo para confirmar tu cuenta."
      );
    } catch (err: any) {
      toast.error(err.message || "Error al registrarse");
    } finally {
      setAuthLoading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="text-muted-foreground">Confirmando tu pago...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <h1 className="text-2xl font-bold text-foreground">
          No pudimos confirmar tu pago
        </h1>
        <p className="text-muted-foreground text-center max-w-md">
          Si realizaste el pago, por favor contáctanos a soporte. Tu pago está
          seguro.
        </p>
        <Button onClick={() => navigate("/rutas")}>
          Volver a certificaciones
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6 md:p-8 space-y-6">
          {/* Success header */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <PartyPopper className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              ¡Pago exitoso!
            </h1>
            <p className="text-muted-foreground">
              Tu inscripción ha sido confirmada para{" "}
              <span className="font-medium text-foreground">
                {enrollmentEmail}
              </span>
            </p>
          </div>

          {/* Auth section */}
          <div className="space-y-4">
            {userExists ? (
              <>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    Ya tienes una cuenta. Inicia sesión para acceder a tu
                    ruta.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Contraseña</Label>
                  <Input
                    type="password"
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  />
                </div>

                <Button
                  onClick={handleLogin}
                  disabled={authLoading}
                  className="w-full gap-2"
                >
                  {authLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LogIn className="w-4 h-4" />
                  )}
                  Iniciar sesión
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Crea tu cuenta para acceder al contenido de la ruta. Tu
                  inscripción ya está vinculada a tu correo.
                </p>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Nombre completo</Label>
                    <Input
                      placeholder="Tu nombre"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <Input
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleRegister}
                  disabled={authLoading}
                  className="w-full gap-2"
                >
                  {authLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  Crear cuenta
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  ¿Ya tienes cuenta?{" "}
                  <button
                    onClick={() => {
                      setMode("login");
                      setUserExists(true);
                    }}
                    className="text-primary hover:underline"
                  >
                    Inicia sesión
                  </button>
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnrollmentResultPage;
